import React, { useState } from "react";
import { Button } from "react-bootstrap";
import { uploadReports } from "@ts/retrieve-data";
import { showToast } from "@ts/modals";
import {
    TReports,
    IReportForm,
    IReportTypes,
    IAdminToggleBtn,
} from "@ts/common-types";
import * as Sentry from "@sentry/react";
import NoDataMessage from "@misc/NoDataMessage";
import ReportsTemplate from "./ReportsTemplate";
import { Input, Select } from "antd";
import { useSelector } from "react-redux";

const ReportForm: React.FC<IReportForm> = ({
    existingReportsData,
    setGetUpdatedReports,
    setSelectedModule,
}) => {
    const [reports, setReports] = useState<Array<TReports>>([]);
    const [reportInputData, setReportInputData] = useState({
        reportId: "",
        reportName: "",
        reportType: null,
    });
    const reportTypesData = useSelector(
        (state: IReportTypes) => state.reportTypesData.value
    );
    const enableRevstack = useSelector(
        (state: IAdminToggleBtn) => state.enableRevstack.value
    );

    const handleAddReport = () => {
        if (
            reportInputData.reportId.trim() !== "" &&
            reportInputData.reportName.trim() !== ""
        ) {
            if (
                existingReportsData &&
                existingReportsData.filter(
                    (report) => report.report_id === reportInputData.reportId
                ).length
            ) {
                showToast("danger", "Report ID already exists!");
                setReportInputData({
                    reportId: "",
                    reportName: "",
                    reportType: null,
                });
                return;
            }

            if (
                reports.filter(
                    (report) => report.id === reportInputData.reportId
                ).length
            ) {
                showToast("danger", "Duplicate ID Not Allowed!");
                setReportInputData({
                    reportId: "",
                    reportName: "",
                    reportType: null,
                });
            } else {
                setReports([
                    ...reports,
                    {
                        id: reportInputData.reportId,
                        name: reportInputData.reportName,
                        type: +reportInputData.reportType.split("-")[0].trim(),
                    },
                ]);
                setReportInputData({
                    reportId: "",
                    reportName: "",
                    reportType: null,
                });
            }
        }
    };

    const handleDeleteReport = (id: string) =>
        setReports(reports.filter((report) => report.id !== id));

    const onSaveClickHandler = async () => {
        try {
            if (reports.length !== 0) {
                const response = await uploadReports(reports);
                if (!response.status) {
                    throw new Error(response.message);
                }
                showToast("success", response.message);
                setGetUpdatedReports((prev) => !prev);
                setSelectedModule("");
                setReportInputData({
                    reportId: "",
                    reportName: "",
                    reportType: null,
                });
                setReports([]);
            }
        } catch (err) {
            console.error(err);
            showToast("danger", (err as Error).message);
            Sentry.captureException(err);
        }
    };

    const handleOnChange = (val: string, identifier: string) => {
        if (identifier === "id") {
            setReportInputData((prev) => ({
                ...prev,
                reportId: val,
            }));
        } else if (identifier === "name") {
            setReportInputData((prev) => ({
                ...prev,
                reportName: val,
            }));
        } else {
            setReportInputData((prev) => ({
                ...prev,
                reportType: val,
            }));
        }
    };

    return (
        <div className="report-form-container">
            <div className="input-container">
                <div className="p-1 ps-2">
                    <label>Report ID</label>
                    <Input
                        placeholder="Report ID"
                        value={reportInputData.reportId}
                        onChange={(e) => handleOnChange(e.target.value, "id")}
                        className="d-flex border"
                    />
                </div>
                <div className="p-1 ps-2">
                    <label>Report Name</label>
                    <Input
                        placeholder="Report Name"
                        value={reportInputData.reportName}
                        onChange={(e) => handleOnChange(e.target.value, "name")}
                        className="d-flex border"
                    />
                </div>
                <div className="p-1 ps-2">
                    <label>Report Type</label>
                    <Select
                        className="d-flex"
                        placeholder="Select Report Type"
                        value={reportInputData.reportType}
                        onChange={(val) => handleOnChange(val, "type")}
                    >
                        {reportTypesData?.map((type) =>
                            !enableRevstack && type.id === 1 ? null : (
                                <option
                                    key={type.id}
                                    value={`${type.id} - ${type.name}`}
                                >
                                    {type.name}
                                </option>
                            )
                        )}
                    </Select>
                </div>
            </div>
            <div className="text-end my-2 mx-1">
                <button
                    className="btn btn-success btn-sm"
                    disabled={
                        !reportInputData.reportId ||
                        !reportInputData.reportName ||
                        !reportInputData.reportType
                    }
                    onClick={handleAddReport}
                >
                    Add Report
                </button>
            </div>
            {reports.length ? (
                <div className="reports-container">
                    <ReportsTemplate
                        {...{ reports, handleDeleteReport, reportTypesData }}
                    />
                    <div className="save-reports-btn-container">
                        <div className="empty-container"></div>
                        <div className="empty-container"></div>
                        <Button
                            className="my-1"
                            variant="primary"
                            size="sm"
                            onClick={onSaveClickHandler}
                        >
                            Save Reports
                        </Button>
                    </div>
                </div>
            ) : (
                <NoDataMessage message="No Reports Added" />
            )}
        </div>
    );
};

export default ReportForm;
